import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, ReactNode, WheelEvent } from 'react';
import { Box, IconButton, Tooltip, makeStyles } from '@material-ui/core';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import ReplayIcon from '@material-ui/icons/Replay';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import type { Simulation, SimulationNodeDatum } from 'd3-force';
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCollide,
  forceCenter,
  forceX,
  forceY,
} from 'd3-force';
import { hierarchicalLayout } from './hierarchicalLayout';

const ZOOM_STEP = 0.2;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;

/**
 * Presentation is resolved by the caller rather than derived here, so this
 * canvas stays agnostic of any particular graph model — a topology document and
 * a raw graph-service payload can both feed it.
 */
export interface ForceGraphNode extends SimulationNodeDatum {
  id: string;
  label: string;
  color: string;
  radius: number;
  /** Drawn as a dashed outline instead of a filled disc. */
  outlined?: boolean;
  boldLabel?: boolean;
  /** Hover text; falls back to the label, which is truncated when drawn. */
  tooltip?: string;
  /** Passed to `onNodeClick` verbatim; nodes without one are not clickable. */
  entityRef?: string;
}

export interface ForceGraphLink {
  id: string;
  source: string | ForceGraphNode;
  target: string | ForceGraphNode;
  color: string;
  width: number;
  dashed?: boolean;
  arrow?: boolean;
  opacity?: number;
  distance: number;
  strength: number;
  /** Relation label used to drive the visibility filter. */
  group?: string;
}

export interface ForceGraphModel {
  nodes: ForceGraphNode[];
  links: ForceGraphLink[];
}

export type GraphLayout = 'force' | 'hierarchical';

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 400,
    overflow: 'hidden',
    background: '#1a1a1a',
    borderRadius: 8,
    border: '1px solid #414141',
  },
  canvas: {
    position: 'relative',
    flex: 1,
    // Without this a tall SVG refuses to shrink and pushes the footer off.
    minHeight: 0,
    overflow: 'hidden',
  },
  footer: {
    flexShrink: 0,
    padding: '8px 12px',
    borderTop: '1px solid #414141',
    background: '#212121',
  },
  controls: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    background: 'rgba(46,46,46,0.9)',
    borderRadius: 6,
    padding: '2px 4px',
    border: '1px solid #414141',
  },
  empty: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#6e7479',
    fontSize: 13,
  },
  relationBar: {
    position: 'absolute',
    top: 8,
    left: 8,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    zIndex: 10,
    maxWidth: 'calc(100% - 200px)',
    background: 'rgba(46,46,46,0.9)',
    borderRadius: 6,
    padding: '4px 8px',
    border: '1px solid #414141',
  },
  relationLabel: { color: '#a0a4a8', fontSize: 11, marginRight: 2 },
  relationChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer',
    border: '1px solid transparent',
    borderRadius: 10,
    padding: '1px 8px',
    fontSize: 11,
    background: 'rgba(255,255,255,0.08)',
    color: '#e4e6e8',
    userSelect: 'none',
  },
  relationChipOff: {
    background: 'transparent',
    border: '1px solid #4a4f54',
    color: '#6e7479',
  },
  relationSwatch: { width: 14, height: 0, borderTopWidth: 2, borderTopStyle: 'solid' },
  tooltip: {
    position: 'absolute',
    zIndex: 20,
    pointerEvents: 'none',
    maxWidth: 320,
    background: 'rgba(20,20,20,0.96)',
    border: '1px solid #5c6470',
    borderRadius: 4,
    padding: '4px 8px',
    color: '#e4e6e8',
    fontSize: 12,
    lineHeight: 1.4,
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  },
}));

function computeContentBox(nodes: ForceGraphNode[]) {
  const pad = 90;
  const xs = nodes.map(n => n.x ?? 0);
  const ys = nodes.map(n => n.y ?? 0);
  const x = Math.min(...xs) - pad;
  const y = Math.min(...ys) - pad;
  return {
    x,
    y,
    width: Math.max(Math.max(...xs) - x + pad, 200),
    height: Math.max(Math.max(...ys) - y + pad, 200),
  };
}

export interface ForceGraphCanvasProps {
  model: ForceGraphModel;
  /** Repulsion between nodes; larger graphs generally need a stronger value. */
  chargeStrength?: number;
  emptyMessage?: string;
  /** Layout the canvas opens with; the user can switch at any time. */
  defaultLayout?: GraphLayout;
  /** Fired on a plain click (not a drag) of a node that carries an `entityRef`. */
  onNodeClick?: (entityRef: string) => void;
  /** Rendered inside the canvas, typically a legend. */
  children?: ReactNode;
}

/**
 * Graph canvas with zoom, pan and drag-to-pin, in either a force-directed or a
 * top-down layered arrangement.
 *
 * Both layouts are resolved synchronously so the result is deterministic and
 * does not animate into place; the simulation only reheats while a node is
 * being dragged, which is what lets linked nodes follow the cursor.
 */
export const ForceGraphCanvas = ({
  model,
  chargeStrength = -520,
  emptyMessage = 'Nothing to display yet.',
  defaultLayout = 'force',
  onNodeClick,
  children,
}: ForceGraphCanvasProps) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentBox, setContentBox] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [settled, setSettled] = useState(0);
  // Incremented on every simulation tick while a drag reheats the layout, so
  // React re-reads the coordinates d3 mutates in place.
  const [, setFrame] = useState(0);
  const [nonce, setNonce] = useState(0);
  const [zoom, setZoom] = useState<number | null>(null); // null = fit-to-screen
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [hover, setHover] = useState<{ text: string; x: number; y: number } | null>(null);
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
  const [layout, setLayout] = useState<GraphLayout>(defaultLayout);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const draggedNode = useRef<ForceGraphNode | null>(null);
  // Click vs. drag is distinguished the same way as the ELK renderer: a node
  // mousedown followed by movement past a small threshold counts as a drag.
  const nodeMouseDownAt = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const simRef = useRef<Simulation<ForceGraphNode, undefined> | null>(null);

  useEffect(() => {
    simRef.current?.stop();
    if (model.nodes.length === 0) return undefined;

    let simulation: Simulation<ForceGraphNode, undefined>;

    if (layout === 'hierarchical') {
      const rows = hierarchicalLayout(model.nodes, model.links);
      for (const node of model.nodes) {
        const position = rows.get(node.id);
        if (!position) continue;
        node.x = position.x;
        node.y = position.y;
        node.vx = 0;
        node.vy = 0;
      }
      // forceLink is what normally swaps the string endpoints for node objects,
      // and it never runs here, so the renderer would find nothing to draw.
      const byId = new Map(model.nodes.map(node => [node.id, node]));
      for (const link of model.links) {
        const source = typeof link.source === 'string' ? byId.get(link.source) : link.source;
        const target = typeof link.target === 'string' ? byId.get(link.target) : link.target;
        if (source) link.source = source;
        if (target) link.target = target;
      }
      // No forces registered: d3 only integrates velocities, so the rows stay
      // put while ticks keep firing to re-render a drag in progress.
      simulation = forceSimulation<ForceGraphNode>(model.nodes).stop();
    } else {
      simulation = forceSimulation<ForceGraphNode>(model.nodes)
        .force(
          'link',
          forceLink<ForceGraphNode, any>(model.links)
            .id(d => d.id)
            .distance(link => (link as ForceGraphLink).distance)
            .strength(link => (link as ForceGraphLink).strength),
        )
        .force('charge', forceManyBody<ForceGraphNode>().strength(chargeStrength))
        .force('collide', forceCollide<ForceGraphNode>().radius(d => d.radius + 18))
        .force('center', forceCenter(0, 0))
        // Weak pull toward the origin for every node, not just the centroid —
        // without this, disconnected components drift arbitrarily far apart.
        .force('x', forceX(0).strength(0.05))
        .force('y', forceY(0).strength(0.05))
        .stop();

      // `simulation.tick()` does not dispatch events, so the tick listener below
      // only fires once a drag reheats the simulation.
      simulation.tick(320);
    }

    simulation.on('tick', () => setFrame(f => f + 1));
    simRef.current = simulation;

    setContentBox(computeContentBox(model.nodes));
    setZoom(null);
    setPan({ x: 0, y: 0 });
    setSettled(s => s + 1);

    return () => {
      simulation.stop();
    };
  }, [model, nonce, chargeStrength, layout]);

  const getEffectiveZoom = useCallback(() => {
    if (zoom !== null) return zoom;
    const el = containerRef.current;
    if (!el) return 1;
    const scaleX = el.clientWidth / contentBox.width;
    const scaleY = el.clientHeight / contentBox.height;
    return Math.min(scaleX, scaleY, 1);
  }, [zoom, contentBox]);

  const handleZoomIn = () => setZoom(Math.min(getEffectiveZoom() + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => setZoom(Math.max(getEffectiveZoom() - ZOOM_STEP, MIN_ZOOM));
  const handleFitToScreen = () => {
    // Recompute bounds so dragged-out nodes are included in the fit.
    if (model.nodes.length > 0) setContentBox(computeContentBox(model.nodes));
    setZoom(null);
    setPan({ x: 0, y: 0 });
  };

  /** Re-running the layout also releases any positions the user pinned by dragging. */
  const handleRerunLayout = () => {
    for (const node of model.nodes) {
      node.fx = null;
      node.fy = null;
    }
    setNonce(n => n + 1);
  };

  const handleToggleLayout = () => {
    for (const node of model.nodes) {
      node.fx = null;
      node.fy = null;
    }
    setLayout(current => (current === 'force' ? 'hierarchical' : 'force'));
  };

  /** Screen coordinates to SVG user units, accounting for zoom, pan and letterboxing. */
  const toSvgPoint = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg || typeof svg.getScreenCTM !== 'function') return null;
    const ctm = svg.getScreenCTM();
    if (!ctm || typeof ctm.inverse !== 'function') return null;
    const inv = ctm.inverse();
    return {
      x: clientX * inv.a + clientY * inv.c + inv.e,
      y: clientX * inv.b + clientY * inv.d + inv.f,
    };
  }, []);

  const handleNodeMouseDown = useCallback(
    (e: MouseEvent, node: ForceGraphNode) => {
      if (e.button !== 0) return;
      // Keep the canvas pan handler from also claiming this gesture.
      e.stopPropagation();
      dragMoved.current = false;
      nodeMouseDownAt.current = { x: e.clientX, y: e.clientY };
      const point = toSvgPoint(e.clientX, e.clientY);
      node.fx = point ? point.x : node.x ?? 0;
      node.fy = point ? point.y : node.y ?? 0;
      draggedNode.current = node;
      setDraggedNodeId(node.id);
      // Reheat so linked nodes follow the one being dragged.
      simRef.current?.alphaTarget(0.3).restart();
    },
    [toSvgPoint],
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP * 0.5 : ZOOM_STEP * 0.5;
      setZoom(z => {
        const current = z ?? getEffectiveZoom();
        return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, current + delta));
      });
    },
    [getEffectiveZoom],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    },
    [pan],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const node = draggedNode.current;
      if (node) {
        const dx = e.clientX - nodeMouseDownAt.current.x;
        const dy = e.clientY - nodeMouseDownAt.current.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragMoved.current = true;
        const point = toSvgPoint(e.clientX, e.clientY);
        if (point) {
          node.fx = point.x;
          node.fy = point.y;
        }
        return;
      }
      if (!dragging) return;
      setPan({
        x: dragStart.current.panX + (e.clientX - dragStart.current.x),
        y: dragStart.current.panY + (e.clientY - dragStart.current.y),
      });
    },
    [dragging, toSvgPoint],
  );

  const handleMouseUp = useCallback(() => {
    if (draggedNode.current) {
      if (!dragMoved.current && onNodeClick && draggedNode.current.entityRef) {
        onNodeClick(draggedNode.current.entityRef);
      }
      // fx/fy are intentionally left set so the node stays where it was dropped.
      simRef.current?.alphaTarget(0);
      draggedNode.current = null;
      setDraggedNodeId(null);
    }
    setDragging(false);
  }, [onNodeClick]);

  /** One entry per distinct relation, in first-seen order, for the filter bar. */
  const relationGroups = useMemo(() => {
    const groups = new Map<string, { label: string; color: string; dashed: boolean }>();
    for (const link of model.links) {
      if (!link.group || groups.has(link.group)) continue;
      groups.set(link.group, {
        label: link.group,
        color: link.color,
        dashed: !!link.dashed,
      });
    }
    return Array.from(groups.values());
  }, [model]);

  const toggleGroup = useCallback((group: string) => {
    setHiddenGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  /**
   * Filtering is render-only: the simulation keeps every link so toggling a
   * relation declutters the view without reshuffling the layout underneath.
   */
  const visibleLinks = useMemo(
    () => model.links.filter(link => !link.group || !hiddenGroups.has(link.group)),
    [model, hiddenGroups],
  );

  const showTooltip = useCallback((e: MouseEvent, node: ForceGraphNode) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({
      text: node.tooltip || node.label,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  if (model.nodes.length === 0) {
    return (
      <Box className={classes.wrapper}>
        <div className={classes.empty}>{emptyMessage}</div>
      </Box>
    );
  }

  const effectiveZoom = getEffectiveZoom();
  const isManualZoom = zoom !== null;

  let vbX = contentBox.x;
  let vbY = contentBox.y;
  let vbW = contentBox.width;
  let vbH = contentBox.height;

  if (isManualZoom && containerRef.current) {
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    vbW = cw / effectiveZoom;
    vbH = ch / effectiveZoom;
    vbX = contentBox.x + (contentBox.width - vbW) / 2 - pan.x / effectiveZoom;
    vbY = contentBox.y + (contentBox.height - vbH) / 2 - pan.y / effectiveZoom;
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.canvas} ref={containerRef}>
        {relationGroups.length > 1 && (
        <Box className={classes.relationBar}>
          <span className={classes.relationLabel}>Relations:</span>
          {relationGroups.map(group => {
            const off = hiddenGroups.has(group.label);
            return (
              <span
                key={group.label}
                role="checkbox"
                aria-checked={!off}
                aria-label={group.label}
                tabIndex={0}
                className={`${classes.relationChip} ${off ? classes.relationChipOff : ''}`}
                onClick={() => toggleGroup(group.label)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') toggleGroup(group.label);
                }}
              >
                <span
                  className={classes.relationSwatch}
                  style={{
                    borderTopColor: off ? '#4a4f54' : group.color,
                    borderTopStyle: group.dashed ? 'dashed' : 'solid',
                  }}
                />
                {group.label}
              </span>
            );
          })}
        </Box>
      )}

      <Box className={classes.controls}>
        <Tooltip
          title={
            layout === 'force' ? 'Switch to hierarchical layout' : 'Switch to force layout'
          }
        >
          <IconButton
            size="small"
            style={{ color: '#a0a4a8' }}
            aria-label={
              layout === 'force' ? 'Switch to hierarchical layout' : 'Switch to force layout'
            }
            onClick={handleToggleLayout}
          >
            {layout === 'force' ? (
              <AccountTreeIcon fontSize="small" />
            ) : (
              <BubbleChartIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in">
          <IconButton size="small" style={{ color: '#a0a4a8' }} onClick={handleZoomIn}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton size="small" style={{ color: '#a0a4a8' }} onClick={handleZoomOut}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to screen">
          <IconButton size="small" style={{ color: '#a0a4a8' }} onClick={handleFitToScreen}>
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Re-run layout (clears dragged positions)">
          <IconButton size="small" style={{ color: '#a0a4a8' }} onClick={handleRerunLayout}>
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <svg
        key={settled}
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <defs>
          <marker
            id="force-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#8a8f94" />
          </marker>
        </defs>

        {visibleLinks.map(link => {
          const source = link.source as ForceGraphNode;
          const target = link.target as ForceGraphNode;
          if (typeof source === 'string' || typeof target === 'string') return null;
          return (
            <line
              key={link.id}
              x1={source.x ?? 0}
              y1={source.y ?? 0}
              x2={target.x ?? 0}
              y2={target.y ?? 0}
              stroke={link.color}
              strokeWidth={link.width}
              strokeDasharray={link.dashed ? '6 4' : undefined}
              markerEnd={link.arrow ? 'url(#force-arrow)' : undefined}
              opacity={link.opacity ?? 0.9}
            />
          );
        })}

        {model.nodes.map(node => {
          const isPinned = node.fx !== null && node.fx !== undefined;
          return (
            <g
              key={node.id}
              onMouseDown={e => handleNodeMouseDown(e, node)}
              onMouseEnter={e => showTooltip(e, node)}
              onMouseMove={e => showTooltip(e, node)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: draggedNodeId === node.id ? 'grabbing' : 'grab' }}
            >
              <circle
                cx={node.x ?? 0}
                cy={node.y ?? 0}
                r={node.radius}
                fill={node.outlined ? 'transparent' : node.color}
                stroke={node.outlined ? '#6e7479' : 'rgba(0,0,0,0.35)'}
                strokeWidth={node.outlined ? 1.5 : 1}
                strokeDasharray={node.outlined ? '4 3' : undefined}
              />
              {/* A pinned node keeps a halo so it is obvious the position was set by hand. */}
              {isPinned && (
                <circle
                  cx={node.x ?? 0}
                  cy={node.y ?? 0}
                  r={node.radius + 4}
                  fill="none"
                  stroke="#e4e6e8"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                  opacity={0.7}
                />
              )}
              <text
                x={node.x ?? 0}
                y={(node.y ?? 0) + node.radius + 14}
                textAnchor="middle"
                fill="#e4e6e8"
                fontSize={11}
                fontFamily="Inter, Arial, sans-serif"
                fontWeight={node.boldLabel ? 600 : 400}
              >
                {node.label.length > 28 ? `${node.label.slice(0, 27)}…` : node.label}
              </text>
            </g>
          );
        })}
      </svg>

        {hover && (
          <div
            className={classes.tooltip}
            style={{
              left: hover.x + 14,
              top: hover.y + 14,
              // Flip back across the cursor rather than spilling out of the canvas.
              transform: hover.x > (containerRef.current?.clientWidth ?? 0) / 2 ? 'translateX(-100%)' : undefined,
            }}
          >
            {hover.text}
          </div>
        )}
      </div>

      {children && <div className={classes.footer}>{children}</div>}
    </div>
  );
};
